import React, { useEffect, useRef, useState } from 'react';
import { Network, Database, Users, Leaf } from 'lucide-react';

const Thesis = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const pillars = [
    {
      icon: Network,
      title: 'Systems',
      description: 'Connected architecture across ventures'
    },
    {
      icon: Database,
      title: 'Data',
      description: 'Intelligence embedded in every decision'
    },
    {
      icon: Users,
      title: 'Community',
      description: 'Products built around people'
    },
    {
      icon: Leaf,
      title: 'Sustainability',
      description: 'Long-term thinking, lasting impact'
    }
  ];

  useEffect(() => {
    const current = sectionRef.current;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (current) {
      observer.observe(current);
    }

    return () => {
      if (current) {
        observer.unobserve(current);
      }
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-12 sm:py-16 md:py-24 bg-gradient-to-b from-white via-white to-slate-50 relative overflow-hidden">
      {/* Floating Gradient Orbs - Reduced on mobile */}
      <div className="absolute top-20 right-1/4 w-64 h-64 sm:w-96 sm:h-96 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full blur-3xl animate-float-orb-1"></div>
      <div className="absolute top-40 left-1/3 w-48 h-48 sm:w-80 sm:h-80 bg-gradient-to-br from-slate-400/15 to-blue-400/15 rounded-full blur-3xl animate-float-orb-2"></div>
      <div className="absolute bottom-32 right-1/3 w-56 h-56 sm:w-72 sm:h-72 bg-gradient-to-br from-purple-400/18 to-slate-400/18 rounded-full blur-3xl animate-float-orb-3"></div>
      <div className="hidden md:block absolute top-1/2 left-1/4 w-64 h-64 bg-gradient-to-br from-blue-400/12 to-slate-500/12 rounded-full blur-3xl animate-float-orb-4"></div>
      <div className="hidden lg:block absolute top-1/3 right-1/2 w-88 h-88 bg-gradient-to-br from-purple-500/14 to-blue-500/14 rounded-full blur-3xl animate-float-orb-2"></div>
      <div className="hidden lg:block absolute bottom-1/4 left-1/2 w-76 h-76 bg-gradient-to-br from-slate-500/16 to-purple-400/16 rounded-full blur-3xl animate-float-orb-3"></div>
      <div className="absolute top-0 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-slate-200/30 rounded-full blur-3xl"></div>
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
        <div className={`text-center mb-12 sm:mb-16 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <h3 className="text-xs sm:text-sm font-semibold text-slate-600 uppercase tracking-wider mb-3 sm:mb-4">Our Thesis</h3>
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4 sm:mb-6 leading-tight max-w-4xl mx-auto px-4">
            We believe the most valuable companies are built by connecting systems thinking with domain expertise.
          </h2>
          <p className="text-base sm:text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed px-4">
            OwlWorkLabs operates at the convergence of automation, sustainability, and community. We're not building disconnected products—we're developing an ecosystem of intelligent solutions that share a common DNA: data-driven decision making, scalable architecture, and measurable real-world outcomes.
          </p>
          <p className="text-base sm:text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed mt-3 sm:mt-4 px-4">
            Each venture starts with a validated problem, moves through rigorous analysis, and emerges as a product designed for impact at scale. We build what we believe the world needs, then prove it works.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
          {pillars.map((pillar, index) => {
            const Icon = pillar.icon;
            return (
              <div 
                key={pillar.title} 
                className={`group flex flex-col items-center text-center p-6 sm:p-8 rounded-2xl bg-white/60 backdrop-blur-sm border border-slate-200/50 hover:bg-white/80 hover:border-slate-300/50 transition-all duration-500 hover:shadow-xl hover:-translate-y-2 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ 
                  transitionDelay: `${(index + 1) * 150}ms`
                }}
              >
                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center mb-3 sm:mb-4 shadow-lg group-hover:shadow-2xl group-hover:scale-110 transition-all duration-300">
                  <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                </div>
                <h4 className="text-lg sm:text-xl font-bold text-slate-900 mb-2">{pillar.title}</h4>
                <p className="text-sm text-slate-600">{pillar.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Thesis;